import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  Clock,
  FileText,
  GraduationCap,
  Home,
  LayoutGrid,
  LifeBuoy,
  Megaphone,
  MessageCircle,
  Moon,
  
  Plus,
  Radar,
  Plug,
  Receipt,
  Settings,
  ShoppingBag,
  Sun,
  Trophy,
  Users,
  Webhook,
  Workflow,
  Zap,
} from 'lucide-react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '@/components/ui/command';
import { useTheme } from '@/providers/theme-provider';
import { useEcommerce, useScheduling, useStudents } from '@/hooks/use-settings';

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  // Show the right world: an e-commerce workspace should never see "New lead" /
  // Meetings / Academy - it wants Orders / Customers / Products / measurement.
  const { enabled: ecomOn } = useEcommerce();
  const { enabled: scheduling } = useScheduling();
  const { enabled: students } = useStudents();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder={ecomOn ? 'Search orders, customers, jump to a page…' : 'Search leads, jump to a page, or run a command…'} />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        {ecomOn ? (
          <>
            <CommandGroup heading="Navigation">
              <CommandItem onSelect={() => go('/dashboard')}>
                <Home />
                <span>Dashboard</span>
                <CommandShortcut>G H</CommandShortcut>
              </CommandItem>
              <CommandItem onSelect={() => go('/commerce')}>
                <ShoppingBag />
                <span>Orders</span>
              </CommandItem>
              <CommandItem onSelect={() => go('/commerce/customers')}>
                <Users />
                <span>Customers</span>
              </CommandItem>
              <CommandItem onSelect={() => go('/commerce/attribution')}>
                <Radar />
                <span>Attribution</span>
              </CommandItem>
            </CommandGroup>

            <CommandSeparator />

            <CommandGroup heading="Measurement">
              <CommandItem onSelect={() => go('/tracking/conversions')}>
                <Trophy />
                <span>Conversions</span>
              </CommandItem>
              <CommandItem onSelect={() => go('/integrations')}>
                <Plug />
                <span>Integrations</span>
              </CommandItem>
              <CommandItem onSelect={() => go('/tracking/attribution')}>
                <Zap />
                <span>Attribution &amp; signals</span>
              </CommandItem>
              <CommandItem onSelect={() => go('/tracking/health')}>
                <LifeBuoy />
                <span>Alerts & diagnostics</span>
              </CommandItem>
              <CommandItem onSelect={() => go('/tracking/webhooks')}>
                <Webhook />
                <span>Webhooks</span>
              </CommandItem>
            </CommandGroup>

            <CommandSeparator />

            <CommandGroup heading="Messaging">
              <CommandItem onSelect={() => go('/whatsapp')}>
                <MessageCircle />
                <span>WhatsApp inbox</span>
              </CommandItem>
              <CommandItem onSelect={() => go('/whatsapp/broadcasts')}>
                <Megaphone />
                <span>WhatsApp broadcast</span>
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup heading="Growth">
              {[['/growth/actions', "Today's Actions"], ['/growth/findings', 'What We Found'], ['/growth/customer-value', 'Customer Value'],
                ['/growth/buyers', 'Who Bought'], ['/growth/leaks', 'Where you lose money'], ['/studio', 'Signal Studio'], ['/growth/channel-roles', 'Channel Roles'],
                ['/growth/funnels', 'Funnels'], ['/growth/creatives', 'Creatives'], ['/commerce/recovery', 'Recovery'], ['/signal-health', 'Signal Health']]
                .map(([to, label]) => (
                  <CommandItem key={to} onSelect={() => go(to)}>
                    <Zap />
                    <span>{label}</span>
                  </CommandItem>
                ))}
            </CommandGroup>
          </>
        ) : (
          <>
            <CommandGroup heading="Quick actions">
              <CommandItem onSelect={() => go('/leads')}>
                <Plus />
                <span>New lead</span>
                <CommandShortcut>N</CommandShortcut>
              </CommandItem>
              <CommandItem onSelect={() => go('/forms')}>
                <Plus />
                <span>New form</span>
              </CommandItem>
            </CommandGroup>

            <CommandSeparator />

            <CommandGroup heading="Navigation">
              <CommandItem onSelect={() => go('/dashboard')}>
                <Home />
                <span>Dashboard</span>
                <CommandShortcut>G H</CommandShortcut>
              </CommandItem>
              <CommandItem onSelect={() => go('/leads')}>
                <LayoutGrid />
                <span>Leads</span>
                <CommandShortcut>G L</CommandShortcut>
              </CommandItem>
              {scheduling && (
                <>
                  <CommandItem onSelect={() => go('/meetings')}>
                    <Calendar />
                    <span>Meetings</span>
                    <CommandShortcut>G M</CommandShortcut>
                  </CommandItem>
                  <CommandItem onSelect={() => go('/follow-ups')}>
                    <Clock />
                    <span>Follow-ups</span>
                    <CommandShortcut>G F</CommandShortcut>
                  </CommandItem>
                </>
              )}
              <CommandItem onSelect={() => go('/forms')}>
                <FileText />
                <span>Forms</span>
              </CommandItem>
              <CommandItem onSelect={() => go('/automations')}>
                <Workflow />
                <span>Workflows</span>
              </CommandItem>
            </CommandGroup>

            <CommandSeparator />

            <CommandGroup heading="Tracking">
              <CommandItem onSelect={() => go('/tracking/webhooks')}>
                <Webhook />
                <span>Webhooks</span>
              </CommandItem>
              <CommandItem onSelect={() => go('/tracking/attribution')}>
                <Zap />
                <span>Attribution</span>
              </CommandItem>
              <CommandItem onSelect={() => go('/tracking/health')}>
                <LifeBuoy />
                <span>Alerts & diagnostics</span>
              </CommandItem>
            </CommandGroup>

            {students && (
              <>
                <CommandSeparator />
                <CommandGroup heading="Academy">
                  <CommandItem onSelect={() => go('/students')}>
                    <GraduationCap />
                    <span>Students</span>
                  </CommandItem>
                  <CommandItem onSelect={() => go('/cohorts')}>
                    <Users />
                    <span>Cohorts</span>
                  </CommandItem>
                  <CommandItem onSelect={() => go('/finance')}>
                    <Receipt />
                    <span>Finance</span>
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </>
        )}

        <CommandSeparator />

        <CommandGroup heading="Preferences">
          <CommandItem onSelect={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
            {theme === 'dark' ? <Sun /> : <Moon />}
            <span>Switch to {theme === 'dark' ? 'light' : 'dark'} mode</span>
            <CommandShortcut>⌘ J</CommandShortcut>
          </CommandItem>
          <CommandItem onSelect={() => go('/settings')}>
            <Settings />
            <span>Settings</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
